import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import {
  deleteMemoryRecord,
  fetchMemoryCategories,
  fetchMemoryRecords,
  upsertMemoryRecord,
} from '~/data/memory'
import {
  assignAgentToOffice,
  createAdapter,
  createSkill,
  fetchAdapters,
  fetchAgentAccount,
  fetchAgentNotebook,
  fetchOffices,
  fetchSkills,
  requestAgentRestart,
  reviewNotebook,
  setAgentAdapter,
  setAgentRunState,
  setAgentSkill,
  setAgentStatus,
  setInstructionMode,
  slugify,
  updateAgentInstructions,
  updateAgentProfile,
  type InstructionMode,
} from '~/data/interact'
import type { Person } from '~/data/types'
import { theme } from '~/theme'

// "Manage agent" on an agent's person card (org owners): tweak an EXISTING
// agent — name, job title, charter — and control its status. The counterpart
// to the "+ hire" desk, which creates brand-new agents.

const MODE_HELP: Record<InstructionMode, string> = {
  LEARNING: 'Lessons it records apply immediately (versioned, revertible).',
  PROPOSE: 'Lessons wait for your approval before taking effect — the default.',
  LOCKED: "Frozen — it can't change its own instructions at all.",
}

export function AgentManage({
  person,
  organizationId,
  getToken,
}: {
  person: Person
  organizationId: string
  getToken: GetToken
}) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(person.name)
  const [jobTitle, setJobTitle] = useState(person.jobTitle ?? '')
  const [charter, setCharter] = useState('')
  const [instructions, setInstructions] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [confirmRetire, setConfirmRetire] = useState(false)
  // Quick-create forms (skills/adapters are ACTIVE immediately on create).
  const [newSkillOpen, setNewSkillOpen] = useState(false)
  const [newSkillName, setNewSkillName] = useState('')
  const [newSkillContent, setNewSkillContent] = useState('')
  const [newAdapterOpen, setNewAdapterOpen] = useState(false)
  const [newAdapterName, setNewAdapterName] = useState('')
  const [newAdapterContent, setNewAdapterContent] = useState('')
  // Zunou Memory (AGENT scope) — Workforce parity.
  const [memCategory, setMemCategory] = useState<string>('')
  const [memFormOpen, setMemFormOpen] = useState(false)
  const [memCat, setMemCat] = useState('')
  const [memKey, setMemKey] = useState('')
  const [memValue, setMemValue] = useState('')

  const client = async () => createClient(await getToken())

  const account = useQuery({
    queryKey: ['agentAccount', organizationId, person.userId],
    queryFn: async () =>
      fetchAgentAccount(await client(), { organizationId, userId: person.userId }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const offices = useQuery({
    queryKey: ['offices', organizationId],
    queryFn: async () => fetchOffices(await client(), organizationId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const activeOffices = (offices.data ?? []).filter((o) => o.status === 'ACTIVE')

  // Prefill the form ONCE per agent (charter isn't on the snapshot). Seeding
  // on every account.data identity would wipe unsaved edits whenever any
  // sibling mutation (attach skill, save instructions…) refetches the account.
  const seededForRef = useRef<string | null>(null)
  useEffect(() => {
    if (account.data && seededForRef.current !== account.data.id) {
      seededForRef.current = account.data.id
      setName(account.data.name)
      setJobTitle(account.data.jobTitle ?? '')
      setCharter(account.data.jobDescription ?? '')
      setInstructions(account.data.instructions ?? '')
    }
  }, [account.data])

  // Training state: learning mode + notebook (what the agent has learned) +
  // any lessons waiting for approval. Only loaded once the panel is open.
  const notebook = useQuery({
    queryKey: ['agentNotebook', account.data?.id],
    queryFn: async () => fetchAgentNotebook(await client(), account.data!.id),
    enabled: open && Boolean(account.data?.id),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const changeMode = useMutation({
    mutationFn: async (mode: InstructionMode) => {
      if (!account.data) return
      await setInstructionMode(await client(), { agentAccountId: account.data.id, mode })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agentNotebook', account.data?.id] })
      refresh()
    },
    onError: (e) => setNote(e instanceof Error ? e.message.slice(0, 140) : 'Mode change failed'),
  })

  const reviewLesson = useMutation({
    mutationFn: async ({ versionId, approve }: { versionId: string; approve: boolean }) =>
      reviewNotebook(await client(), {
        versionId,
        approve,
        note: 'Reviewed in Glass Office',
      }),
    onSuccess: (_d, v) => {
      setNote(v.approve ? 'Lesson approved ✓' : 'Lesson rejected')
      setTimeout(() => setNote(null), 2500)
      void queryClient.invalidateQueries({ queryKey: ['agentNotebook', account.data?.id] })
      void queryClient.invalidateQueries({ queryKey: ['agentRequests', organizationId] })
    },
    onError: (e) => setNote(e instanceof Error ? e.message.slice(0, 140) : 'Review failed'),
  })

  // The org's skill/adapter libraries — only loaded once the panel is open.
  const skillLib = useQuery({
    queryKey: ['skills', organizationId],
    queryFn: async () => fetchSkills(await client(), organizationId),
    enabled: open,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const adapterLib = useQuery({
    queryKey: ['adapters', organizationId],
    queryFn: async () => fetchAdapters(await client(), organizationId),
    enabled: open,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: ['agentAccount', organizationId, person.userId],
    })
    void queryClient.invalidateQueries({ queryKey: ['snapshot', organizationId] })
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!account.data) return
      await updateAgentProfile(await client(), {
        agentAccountId: account.data.id,
        name: name.trim() || account.data.name,
        jobTitle: jobTitle.trim(),
        jobDescription: charter.trim(),
      })
    },
    onSuccess: () => {
      setNote('Saved ✓')
      setTimeout(() => setNote(null), 2500)
      refresh()
    },
    onError: (e) => setNote(e instanceof Error ? e.message.slice(0, 140) : 'Save failed'),
  })

  const flipStatus = useMutation({
    mutationFn: async (status: 'ACTIVE' | 'SUSPENDED' | 'RETIRED') => {
      if (!account.data) return
      await setAgentStatus(await client(), { agentAccountId: account.data.id, status })
    },
    onSuccess: () => {
      setConfirmRetire(false)
      refresh()
    },
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Status change failed'),
  })

  const moveOffice = useMutation({
    mutationFn: async (officeId: string | null) => {
      if (!account.data) return
      await assignAgentToOffice(await client(), {
        agentAccountId: account.data.id,
        officeId,
      })
    },
    onSuccess: refresh,
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Office change failed'),
  })

  const runControl = useMutation({
    mutationFn: async (action: 'RUNNING' | 'PAUSED' | 'STOPPED' | 'RESTART') => {
      if (!account.data) return
      if (action === 'RESTART') {
        await requestAgentRestart(await client(), account.data.id)
      } else {
        await setAgentRunState(await client(), {
          agentAccountId: account.data.id,
          runState: action,
        })
      }
    },
    onSuccess: refresh,
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Run control failed'),
  })

  const saveInstructions = useMutation({
    mutationFn: async () => {
      if (!account.data) return
      await updateAgentInstructions(await client(), {
        agentAccountId: account.data.id,
        content: instructions,
      })
    },
    onSuccess: () => {
      setNote('Instructions saved ✓')
      setTimeout(() => setNote(null), 2500)
      refresh()
    },
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Instructions save failed'),
  })

  const toggleSkill = useMutation({
    mutationFn: async ({ skillId, attached }: { skillId: string; attached: boolean }) => {
      if (!account.data) return
      await setAgentSkill(await client(), { agentAccountId: account.data.id, skillId, attached })
    },
    onSuccess: refresh,
    onError: (e) => setNote(e instanceof Error ? e.message.slice(0, 140) : 'Skill change failed'),
  })

  const toggleAdapter = useMutation({
    mutationFn: async ({ adapterId, attached }: { adapterId: string; attached: boolean }) => {
      if (!account.data) return
      await setAgentAdapter(await client(), {
        agentAccountId: account.data.id,
        adapterId,
        attached,
      })
    },
    onSuccess: refresh,
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Adapter change failed'),
  })

  const agentAccountId = account.data?.id
  const memCats = useQuery({
    queryKey: ['agentMemCats', agentAccountId],
    queryFn: async () =>
      fetchMemoryCategories(await client(), {
        organizationId,
        scopeType: 'AGENT',
        scopeId: agentAccountId!,
      }),
    enabled: Boolean(agentAccountId),
    refetchInterval: 60_000,
    retry: false,
  })
  const memRecords = useQuery({
    queryKey: ['agentMemRecords', agentAccountId, memCategory],
    queryFn: async () =>
      fetchMemoryRecords(await client(), {
        organizationId,
        scopeType: 'AGENT',
        scopeId: agentAccountId!,
        category: memCategory || undefined,
      }),
    enabled: Boolean(agentAccountId),
    refetchInterval: 60_000,
    retry: false,
  })
  const refreshMemory = () => {
    void queryClient.invalidateQueries({ queryKey: ['agentMemCats', agentAccountId] })
    void queryClient.invalidateQueries({ queryKey: ['agentMemRecords', agentAccountId] })
  }
  const saveFact = useMutation({
    mutationFn: async () => {
      if (!agentAccountId) return
      await upsertMemoryRecord(await client(), {
        organizationId,
        scopeType: 'AGENT',
        scopeId: agentAccountId,
        category: memCat.trim() || 'general',
        key: memKey.trim(),
        value: memValue.trim(),
      })
    },
    onSuccess: () => {
      setMemFormOpen(false)
      setMemKey('')
      setMemValue('')
      setNote('Fact saved ✓')
      setTimeout(() => setNote(null), 2000)
      refreshMemory()
    },
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Memory save failed'),
  })
  const removeFact = useMutation({
    mutationFn: async (id: string) => deleteMemoryRecord(await client(), id),
    onSuccess: refreshMemory,
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Memory delete failed'),
  })

  // Create in the org library, then attach to this agent in one flow.
  const quickCreateSkill = useMutation({
    mutationFn: async () => {
      if (!account.data) return
      const c = await client()
      const { id } = await createSkill(c, {
        organizationId,
        name: newSkillName.trim(),
        slug: slugify(newSkillName),
        content: newSkillContent.trim(),
      })
      await setAgentSkill(c, { agentAccountId: account.data.id, skillId: id, attached: true })
    },
    onSuccess: () => {
      setNewSkillOpen(false)
      setNewSkillName('')
      setNewSkillContent('')
      setNote('Skill created & attached ✓')
      setTimeout(() => setNote(null), 2500)
      void queryClient.invalidateQueries({ queryKey: ['skills', organizationId] })
      refresh()
    },
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Skill create failed'),
  })

  const quickCreateAdapter = useMutation({
    mutationFn: async () => {
      if (!account.data) return
      const c = await client()
      const { id } = await createAdapter(c, {
        organizationId,
        name: newAdapterName.trim(),
        slug: slugify(newAdapterName),
        content: newAdapterContent.trim(),
      })
      await setAgentAdapter(c, {
        agentAccountId: account.data.id,
        adapterId: id,
        attached: true,
      })
    },
    onSuccess: () => {
      setNewAdapterOpen(false)
      setNewAdapterName('')
      setNewAdapterContent('')
      setNote('Adapter created & attached ✓')
      setTimeout(() => setNote(null), 2500)
      void queryClient.invalidateQueries({ queryKey: ['adapters', organizationId] })
      refresh()
    },
    onError: (e) =>
      setNote(e instanceof Error ? e.message.slice(0, 140) : 'Adapter create failed'),
  })

  // Not an agent account we can see (or not an owner) — render nothing.
  if (account.isLoading || account.isError || !account.data) return null
  const status = account.data.status

  return (
    <div style={{ marginTop: 12 }}>
      <button onClick={() => setOpen((v) => !v)} style={toggleBtn}>
        ✦ Manage agent {open ? '▾' : '▸'}
        {account.data.canManageAgents && (
          <span
            title="HR manager — this agent can hire, seat and skill other agents"
            style={{
              marginLeft: 8,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: '#f0c464',
              background: '#2a2210',
              border: '1px solid #4a3d1e',
              borderRadius: 5,
              padding: '1px 6px',
            }}
          >
            🎩 HR
          </span>
        )}
        {account.data.roleKind === 'PERSONAL' && (
          <span
            title={`Personal agent - runs on ${
              account.data.hiringManagerName
                ? `${account.data.hiringManagerName}'s`
                : "its owner's"
            } own computer, as them`}
            style={{
              marginLeft: 8,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: '#5DCAA5',
              background: 'rgba(29, 158, 117, 0.16)',
              border: '1px solid rgba(29, 158, 117, 0.45)',
              borderRadius: 5,
              padding: '1px 6px',
            }}
          >
            💻 Personal
          </span>
        )}
        {account.data.steward && (
          <span
            title={`Steward - looks after ${
              account.data.steward.principalName
                ? `${account.data.steward.principalName}'s`
                : "its principal's"
            } account for them${
              account.data.steward.staff ? ' with Zunou staff tools' : ''
            }`}
            style={{
              marginLeft: 8,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: '#85B7EB',
              background: 'rgba(55, 138, 221, 0.16)',
              border: '1px solid rgba(55, 138, 221, 0.45)',
              borderRadius: 5,
              padding: '1px 6px',
            }}
          >
            {account.data.steward.staff ? '🪪 Steward · staff' : '🪪 Steward'}
          </span>
        )}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 10,
            color:
              status === 'ACTIVE'
                ? theme.online
                : status === 'SUSPENDED'
                  ? '#ffb86b'
                  : theme.textDim,
          }}
        >
          {status.toLowerCase()}
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          <label style={label}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} style={input} />
          </label>
          <label style={label}>
            Job title
            <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} maxLength={120} style={input} />
          </label>
          <label style={label}>
            Role description
            <textarea
              value={charter}
              onChange={(e) => setCharter(e.target.value)}
              maxLength={2000}
              rows={3}
              style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            style={{ ...saveBtn, opacity: save.isPending ? 0.6 : 1 }}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>

          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {status === 'ACTIVE' ? (
              <button
                onClick={() => flipStatus.mutate('SUSPENDED')}
                disabled={flipStatus.isPending}
                style={ghostSmall}
              >
                Suspend
              </button>
            ) : (
              <button
                onClick={() => flipStatus.mutate('ACTIVE')}
                disabled={flipStatus.isPending}
                style={{ ...ghostSmall, color: theme.online }}
              >
                Reactivate
              </button>
            )}
            {status !== 'RETIRED' && (
              <button
                onClick={() => {
                  if (!confirmRetire) {
                    setConfirmRetire(true)
                    return
                  }
                  flipStatus.mutate('RETIRED')
                }}
                disabled={flipStatus.isPending}
                style={{ ...ghostSmall, color: confirmRetire ? '#ff8a8a' : theme.textDim }}
              >
                {confirmRetire ? 'Really retire?' : 'Retire'}
              </button>
            )}
          </div>
          {/* The agent's LIFE: which office box it lives in + its run state. */}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: theme.textDim, marginBottom: 6 }}>
              Office & runtime
            </div>
            {activeOffices.length > 0 || account.data.office ? (
              <select
                value={account.data.office?.id ?? 'none'}
                onChange={(e) =>
                  moveOffice.mutate(e.target.value === 'none' ? null : e.target.value)
                }
                disabled={moveOffice.isPending}
                style={{ ...input, marginTop: 0, appearance: 'auto' as never }}
              >
                {activeOffices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
                {account.data.office &&
                  !activeOffices.some((o) => o.id === account.data?.office?.id) && (
                    <option value={account.data.office.id}>{account.data.office.name}</option>
                  )}
                <option value="none">No office (can't run)</option>
              </select>
            ) : (
              <div style={{ fontSize: 11, color: theme.textDim }}>
                No office box in this org — the agent exists but can't run until
                one is set up.
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 6,
                  background: '#101218',
                  color:
                    account.data.runState === 'RUNNING'
                      ? theme.online
                      : account.data.runState === 'PAUSED'
                        ? '#ffb86b'
                        : theme.textDim,
                }}
              >
                {account.data.runState.toLowerCase()}
              </span>
              {account.data.runState === 'RUNNING' ? (
                <button
                  onClick={() => runControl.mutate('PAUSED')}
                  disabled={runControl.isPending}
                  style={ghostSmall}
                >
                  Pause
                </button>
              ) : (
                <button
                  onClick={() => runControl.mutate('RUNNING')}
                  disabled={runControl.isPending || !account.data.office}
                  title={account.data.office ? 'Start on the box' : 'Needs an office first'}
                  style={{ ...ghostSmall, color: account.data.office ? theme.online : theme.textDim }}
                >
                  Start
                </button>
              )}
              <button
                onClick={() => runControl.mutate('RESTART')}
                disabled={runControl.isPending || !account.data.office}
                title={account.data.office ? 'Force a fresh relaunch on next sync' : 'Needs an office first'}
                style={ghostSmall}
              >
                Restart
              </button>
            </div>
          </div>

          {/* TRAINING: the learning-mode gate + the agent's own notebook —
              the lessons it records about how to work here, human-reviewed. */}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${theme.border}` }}>
            <div style={subTitle}>
              Training
              {(notebook.data?.pending.length ?? 0) > 0 && (
                <span style={{ color: '#ffb020' }}>
                  {' '}· {notebook.data!.pending.length} awaiting you
                </span>
              )}
            </div>
            {notebook.isError && (
              <div style={{ fontSize: 11, color: theme.textDim }}>
                Training state not available for this account.
              </div>
            )}
            {notebook.data && (
              <>
                <div style={{ display: 'flex', gap: 4 }}>
                  {(
                    [
                      ['LEARNING', '🎓 Learning'],
                      ['PROPOSE', '✍ Propose'],
                      ['LOCKED', '🔒 Locked'],
                    ] as Array<[InstructionMode, string]>
                  ).map(([m, label]) => (
                    <button
                      key={m}
                      onClick={() => changeMode.mutate(m)}
                      disabled={changeMode.isPending}
                      style={notebook.data?.mode === m ? modeChipActive : modeChip}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: theme.textDim, marginTop: 4 }}>
                  {MODE_HELP[notebook.data.mode]}
                </div>

                {notebook.data.pending.map((p) => (
                  <div key={p.id} style={pendingBox}>
                    <div style={{ fontSize: 10.5, color: '#ffb020', fontWeight: 700, marginBottom: 4 }}>
                      Wants to remember:
                    </div>
                    <div style={notebookBody}>{p.content}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button
                        onClick={() => reviewLesson.mutate({ versionId: p.id, approve: true })}
                        disabled={reviewLesson.isPending}
                        style={{ ...ghostSmall, color: theme.online, borderColor: '#2c5a40' }}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => reviewLesson.mutate({ versionId: p.id, approve: false })}
                        disabled={reviewLesson.isPending}
                        style={{ ...ghostSmall, color: '#ff9a9a', borderColor: '#5a2c2c' }}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                ))}

                {notebook.data.applied?.content ? (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ fontSize: 11, color: theme.textDim, cursor: 'pointer' }}>
                      Notebook — what it has learned ({notebook.data.historyCount} versions)
                    </summary>
                    <div style={{ ...notebookBody, marginTop: 4 }}>
                      {notebook.data.applied.content}
                    </div>
                  </details>
                ) : (
                  notebook.data.pending.length === 0 && (
                    <div style={{ fontSize: 11, color: theme.textDim, marginTop: 6 }}>
                      Notebook empty — it hasn't recorded any lessons yet.
                    </div>
                  )
                )}
              </>
            )}
          </div>

          {/* The agent's BRAIN: its CLAUDE.md + attached skills and adapters —
              what actually lands on the box at the next sync. */}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${theme.border}` }}>
            <div style={subTitle}>Instructions (CLAUDE.md)</div>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="No instructions yet — this markdown becomes the agent's CLAUDE.md on its box."
              maxLength={65535}
              rows={6}
              style={{ ...input, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 11, lineHeight: 1.5 }}
            />
            <button
              onClick={() => saveInstructions.mutate()}
              disabled={
                saveInstructions.isPending ||
                instructions === (account.data.instructions ?? '')
              }
              style={{
                ...saveBtn,
                marginTop: 6,
                opacity:
                  saveInstructions.isPending ||
                  instructions === (account.data.instructions ?? '')
                    ? 0.55
                    : 1,
              }}
            >
              {saveInstructions.isPending ? 'Saving…' : 'Save instructions (new version)'}
            </button>

            <div style={{ ...subTitle, marginTop: 12 }}>
              Skills ({account.data.skills.length})
            </div>
            <div style={chipWrap}>
              {account.data.skills.map((s) => (
                <span key={s.id} style={attachChip} title={s.slug}>
                  {s.name}
                  <button
                    onClick={() => toggleSkill.mutate({ skillId: s.id, attached: false })}
                    disabled={toggleSkill.isPending}
                    style={chipX}
                    title="Detach skill"
                  >
                    ×
                  </button>
                </span>
              ))}
              {account.data.skills.length === 0 && (
                <span style={{ fontSize: 11, color: theme.textDim }}>No skills attached.</span>
              )}
            </div>
            {(skillLib.data ?? []).filter(
              (s) => !account.data?.skills.some((a) => a.id === s.id),
            ).length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value)
                    toggleSkill.mutate({ skillId: e.target.value, attached: true })
                }}
                disabled={toggleSkill.isPending}
                style={{ ...input, marginTop: 6, appearance: 'auto' as never }}
              >
                <option value="">+ Attach a skill…</option>
                {(skillLib.data ?? [])
                  .filter((s) => !account.data?.skills.some((a) => a.id === s.id))
                  .map((s) => (
                    <option key={s.id} value={s.id} title={s.description ?? undefined}>
                      {s.name}
                    </option>
                  ))}
              </select>
            )}
            {!newSkillOpen ? (
              <button onClick={() => setNewSkillOpen(true)} style={newBtn}>
                + New skill…
              </button>
            ) : (
              <div style={newForm}>
                <input
                  value={newSkillName}
                  onChange={(e) => setNewSkillName(e.target.value)}
                  placeholder="Skill name (e.g. Weekly report)"
                  maxLength={250}
                  autoFocus
                  style={input}
                />
                {slugify(newSkillName) && (
                  <div style={slugHint}>.claude/skills/{slugify(newSkillName)}</div>
                )}
                <textarea
                  value={newSkillContent}
                  onChange={(e) => setNewSkillContent(e.target.value)}
                  placeholder="SKILL.md markdown — when and how to use this skill."
                  maxLength={65535}
                  rows={4}
                  style={{ ...input, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    onClick={() => quickCreateSkill.mutate()}
                    disabled={
                      quickCreateSkill.isPending ||
                      !slugify(newSkillName) ||
                      !newSkillContent.trim()
                    }
                    style={{
                      ...saveBtn,
                      marginTop: 0,
                      flex: 1,
                      opacity:
                        quickCreateSkill.isPending ||
                        !slugify(newSkillName) ||
                        !newSkillContent.trim()
                          ? 0.55
                          : 1,
                    }}
                  >
                    {quickCreateSkill.isPending ? 'Creating…' : 'Create & attach'}
                  </button>
                  <button onClick={() => setNewSkillOpen(false)} style={{ ...ghostSmall, flex: 'none' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={{ ...subTitle, marginTop: 12 }}>
              Adapters ({account.data.adapters.length})
            </div>
            <div style={chipWrap}>
              {account.data.adapters.map((a) => (
                <span key={a.id} style={attachChip} title={a.slug}>
                  {a.name}
                  <button
                    onClick={() => toggleAdapter.mutate({ adapterId: a.id, attached: false })}
                    disabled={toggleAdapter.isPending}
                    style={chipX}
                    title="Detach adapter"
                  >
                    ×
                  </button>
                </span>
              ))}
              {account.data.adapters.length === 0 && (
                <span style={{ fontSize: 11, color: theme.textDim }}>No adapters attached.</span>
              )}
            </div>
            {(adapterLib.data ?? []).filter(
              (a) => !account.data?.adapters.some((x) => x.id === a.id),
            ).length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value)
                    toggleAdapter.mutate({ adapterId: e.target.value, attached: true })
                }}
                disabled={toggleAdapter.isPending}
                style={{ ...input, marginTop: 6, appearance: 'auto' as never }}
              >
                <option value="">+ Attach an adapter…</option>
                {(adapterLib.data ?? [])
                  .filter((a) => !account.data?.adapters.some((x) => x.id === a.id))
                  .map((a) => (
                    <option key={a.id} value={a.id} title={a.description ?? undefined}>
                      {a.name}
                    </option>
                  ))}
              </select>
            )}
            {!newAdapterOpen ? (
              <button onClick={() => setNewAdapterOpen(true)} style={newBtn}>
                + New adapter…
              </button>
            ) : (
              <div style={newForm}>
                <input
                  value={newAdapterName}
                  onChange={(e) => setNewAdapterName(e.target.value)}
                  placeholder="Adapter name (e.g. House style)"
                  maxLength={250}
                  autoFocus
                  style={input}
                />
                {slugify(newAdapterName) && (
                  <div style={slugHint}>.claude/adapters/{slugify(newAdapterName)}</div>
                )}
                <textarea
                  value={newAdapterContent}
                  onChange={(e) => setNewAdapterContent(e.target.value)}
                  placeholder="ADAPTER.md markdown — reusable context composed into CLAUDE.md."
                  maxLength={65535}
                  rows={4}
                  style={{ ...input, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    onClick={() => quickCreateAdapter.mutate()}
                    disabled={
                      quickCreateAdapter.isPending ||
                      !slugify(newAdapterName) ||
                      !newAdapterContent.trim()
                    }
                    style={{
                      ...saveBtn,
                      marginTop: 0,
                      flex: 1,
                      opacity:
                        quickCreateAdapter.isPending ||
                        !slugify(newAdapterName) ||
                        !newAdapterContent.trim()
                          ? 0.55
                          : 1,
                    }}
                  >
                    {quickCreateAdapter.isPending ? 'Creating…' : 'Create & attach'}
                  </button>
                  <button onClick={() => setNewAdapterOpen(false)} style={{ ...ghostSmall, flex: 'none' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={{ ...subTitle, marginTop: 12 }}>
              Memory ({(memCats.data ?? []).reduce((s, c) => s + c.count, 0)})
            </div>
            {(memCats.data ?? []).length > 1 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                <button
                  onClick={() => setMemCategory('')}
                  style={memCategory === '' ? memChipActive : memChip}
                >
                  all
                </button>
                {(memCats.data ?? []).map((c) => (
                  <button
                    key={c.category}
                    onClick={() =>
                      setMemCategory(memCategory === c.category ? '' : c.category)
                    }
                    style={memCategory === c.category ? memChipActive : memChip}
                  >
                    {c.category} · {c.count}
                  </button>
                ))}
              </div>
            )}
            {(memRecords.data?.records ?? []).length === 0 && !memRecords.isLoading && (
              <div style={{ fontSize: 11, color: theme.textDim }}>
                Nothing remembered yet.
              </div>
            )}
            {(memRecords.data?.records ?? []).slice(0, 30).map((r) => (
              <div key={r.id} style={memRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={memKeyStyle}>
                    {r.pinned ? '⭐ ' : ''}
                    {r.category}/{r.key}
                  </div>
                  <div style={memValueStyle} title={r.value}>
                    {r.value}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMemFormOpen(true)
                    setMemCat(r.category)
                    setMemKey(r.key)
                    setMemValue(r.value)
                  }}
                  style={chipX}
                  title="Edit this fact"
                >
                  ✎
                </button>
                <button
                  onClick={() => removeFact.mutate(r.id)}
                  disabled={removeFact.isPending}
                  style={chipX}
                  title="Forget this fact"
                >
                  ×
                </button>
              </div>
            ))}
            {!memFormOpen ? (
              <button onClick={() => setMemFormOpen(true)} style={newBtn}>
                + New fact…
              </button>
            ) : (
              <div style={newForm}>
                <div style={{ display: 'flex', gap: 5 }}>
                  <input
                    value={memCat}
                    onChange={(e) => setMemCat(e.target.value)}
                    placeholder="category"
                    list="mem-cats"
                    style={{ ...input, flex: 1 }}
                  />
                  <datalist id="mem-cats">
                    {(memCats.data ?? []).map((c) => (
                      <option key={c.category} value={c.category} />
                    ))}
                  </datalist>
                  <input
                    value={memKey}
                    onChange={(e) => setMemKey(e.target.value)}
                    placeholder="key"
                    style={{ ...input, flex: 1 }}
                  />
                </div>
                <textarea
                  value={memValue}
                  onChange={(e) => setMemValue(e.target.value)}
                  placeholder="The fact itself…"
                  rows={3}
                  style={{ ...input, marginTop: 5, resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    onClick={() => saveFact.mutate()}
                    disabled={saveFact.isPending || !memKey.trim() || !memValue.trim()}
                    style={{
                      ...saveBtn,
                      marginTop: 0,
                      flex: 1,
                      opacity:
                        saveFact.isPending || !memKey.trim() || !memValue.trim() ? 0.55 : 1,
                    }}
                  >
                    {saveFact.isPending ? 'Saving…' : 'Save fact'}
                  </button>
                  <button onClick={() => setMemFormOpen(false)} style={{ ...ghostSmall, flex: 'none' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {note && (
            <div
              style={{
                fontSize: 11,
                marginTop: 6,
                color: note.includes('✓') ? theme.online : '#ff8a8a',
              }}
            >
              {note}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const toggleBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  background: '#191634',
  border: '1px solid #34306b',
  color: '#c9c3ff',
  borderRadius: 9,
  fontSize: 12.5,
  fontWeight: 600,
  padding: '8px 10px',
  cursor: 'pointer',
  gap: 6,
}
const label: React.CSSProperties = {
  display: 'block',
  marginTop: 8,
  fontSize: 11,
  fontWeight: 600,
  color: theme.textDim,
}
const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 3,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  fontSize: 12.5,
  padding: '7px 9px',
  outline: 'none',
}
const saveBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 10,
  background: '#5b54c8',
  color: '#eef0ff',
  border: 'none',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 700,
  padding: '8px 10px',
  cursor: 'pointer',
}
const ghostSmall: React.CSSProperties = {
  flex: 1,
  background: 'none',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 8,
  fontSize: 11.5,
  padding: '6px 8px',
  cursor: 'pointer',
}
const subTitle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.textDim,
  marginBottom: 6,
}
const chipWrap: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5,
}
const attachChip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: '#191634',
  border: '1px solid #34306b',
  color: '#c9c3ff',
  borderRadius: 999,
  fontSize: 11,
  padding: '2px 4px 2px 9px',
}
const chipX: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#8a84c8',
  cursor: 'pointer',
  fontSize: 13,
  lineHeight: 1,
  padding: '0 4px',
}
const newBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  background: 'none',
  border: `1px dashed ${theme.border}`,
  color: theme.textDim,
  borderRadius: 8,
  fontSize: 11.5,
  padding: '6px 8px',
  cursor: 'pointer',
  textAlign: 'left',
}
const newForm: React.CSSProperties = {
  marginTop: 6,
  padding: 8,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
}
const modeChip: React.CSSProperties = {
  flex: 1,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 8,
  fontSize: 10.5,
  fontWeight: 600,
  padding: '5px 4px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}
const modeChipActive: React.CSSProperties = {
  ...modeChip,
  background: '#191634',
  border: '1px solid #5b54c8',
  color: '#c9c3ff',
}
const pendingBox: React.CSSProperties = {
  marginTop: 8,
  padding: 8,
  background: '#241f12',
  border: '1px solid #4a3d1e',
  borderRadius: 9,
}
const notebookBody: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 10.5,
  lineHeight: 1.5,
  color: '#d5dae4',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  maxHeight: 140,
  overflowY: 'auto',
  background: '#0b0d12',
  border: `1px solid ${theme.border}`,
  borderRadius: 7,
  padding: '5px 7px',
}
const slugHint: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 9.5,
  color: theme.textDim,
  marginTop: 3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const memChip: React.CSSProperties = {
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 999,
  fontSize: 10,
  padding: '3px 9px',
  cursor: 'pointer',
}
const memChipActive: React.CSSProperties = {
  ...memChip,
  color: '#c9c3ff',
  borderColor: '#34306b',
  background: '#191634',
}
const memRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  padding: '6px 8px',
  marginTop: 4,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
}
const memKeyStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 10,
  color: '#c9c3ff',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const memValueStyle: React.CSSProperties = {
  fontSize: 11,
  color: theme.text,
  lineHeight: 1.4,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as never,
}
